import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, SquareArrowOutUpRight, Trash2 } from 'lucide-react';
import { TaskDetail, type TaskDetailData } from './TaskDetail';
import { apiDelete } from '../../lib/api';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { RemindMeButton } from '../RemindMeButton';

/**
 * TaskModal — full editable task detail rendered in a dialog, so the board
 * can open a task without navigating away. Renders the same `TaskDetail`
 * used by the `/tasks/:id` route, so there is one editor implementation.
 * Mirrors the page's Saving/RemindMeButton/Delete row in the modal top bar,
 * but Delete here closes the modal and refreshes the board instead of
 * navigating away.
 */
export function TaskModal({ taskId, onClose, onChanged }: {
  taskId: string; onClose: () => void; onChanged?: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const [saving, setSaving] = useState(false);
  const [task, setTask] = useState<TaskDetailData | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /* ── Delete ─────────────────────────────────────────── */

  async function handleDelete() {
    if (!await confirm({ title: 'Delete this task?', message: 'This cannot be undone.', variant: 'danger', confirmLabel: 'Delete' })) return;
    try {
      await apiDelete(`/tasks/${taskId}`);
      toast.success('Task deleted');
      onClose();
      onChanged?.();
    } catch (err) {
      console.error('Failed to delete task:', err);
      toast.error('Failed to delete task');
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-bg-surface border border-border-hover rounded-xl w-full max-w-2xl my-8"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <Link
            to={`/tasks/${taskId}`}
            onClick={onClose}
            className="inline-flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary"
          >
            <SquareArrowOutUpRight size={13} /> Open full page
          </Link>
          <div className="flex items-center gap-2">
            {saving && <span className="text-xs text-accent">Saving...</span>}
            {task && <RemindMeButton entityType="task" entityId={taskId} entityTitle={task.title} />}
            <button
              onClick={handleDelete}
              className="flex items-center gap-1 px-2 py-1 text-xs text-text-muted hover:text-danger hover:bg-bg-hover rounded transition-colors"
            >
              <Trash2 size={14} /> Delete
            </button>
            <button onClick={onClose} className="text-text-muted hover:text-text-primary" aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="p-5">
          <TaskDetail taskId={taskId} onChanged={onChanged} onSavingChange={setSaving} onTaskLoaded={setTask} />
        </div>
      </div>
    </div>
  );
}
